import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { watchProposal, ensureAnon } from "../services/proposals";
import type { ProposalDoc, ProposalOutput, ProposalStatus } from "../types";
import { analytics } from "../firebase";
import { logEvent } from "firebase/analytics";
import Footer from "../components/Footer";
import { genProposalServer, startDevPlanGenerationServer, updateProposalSectionServer } from "../services/functions";
import { useMutationAuthGate } from "../hooks/useMutationAuthGate";
import { buildPricingAssessment } from "../utils/pricingEngine";

type KeyFeature = ProposalOutput["keyFeatures"][number];
type DeliveryPhase = ProposalOutput["deliveryPlan"][number];
type PricingPackage = ProposalOutput["pricingPackages"][number];
type ProposalSectionKey = keyof ProposalOutput;
type ImproveMode = "simpler" | "reduce" | "faster";
type EditKind = "text" | "list" | "features" | "delivery" | "pricing";
type EditState = { key: ProposalSectionKey; label: string; kind: EditKind };

const LOADING_STAGES = [
  "Understanding your idea…",
  "Designing solution…",
  "Structuring features…",
];

const IMPROVE_ACTIONS: { mode: ImproveMode; label: string }[] = [
  { mode: "simpler", label: "Make this simpler" },
  { mode: "reduce", label: "Reduce scope" },
  { mode: "faster", label: "Make this faster to build" },
];

const COMPANY_LOGO_URL = `${process.env.PUBLIC_URL || ""}/company-logo.png`;
const COMPANY_NAME = "App Robot, LLC";
const COMPANY_WEBSITE = "https://app-robot.com";
const COMPANY_ADDRESS = "1660 Soldiers Field Rd Ste7 #1010, Boston, MA";
const COMPANY_CONTACT_EMAIL = "support@app-robot.com";

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value.trim() : fallback;
}

function toList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => asString(item)).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function firstSentence(text: string): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const parts = normalized.match(/[^.!?]+[.!?]?/g) ?? [normalized];
  return parts[0]?.trim() ?? normalized;
}

function sentenceLimit(text: string, maxSentences: number): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const sentences = normalized.match(/[^.!?]+[.!?]?/g) ?? [normalized];
  return sentences.slice(0, maxSentences).join(" ").trim();
}

function clampWeeks(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(1, Math.round(numeric));
}

function normalizePriority(value: unknown): "MVP" | "Next" {
  return value === "Next" ? "Next" : "MVP";
}

function normalizeKeyFeatures(value: unknown): KeyFeature[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => ({
        title: asString((item as any)?.title),
        desc: asString((item as any)?.desc),
        priority: normalizePriority((item as any)?.priority),
      }))
      .filter((item) => item.title || item.desc);
  }
  if (typeof value === "string") {
    return toList(value).map((line, idx) => ({
      title: firstSentence(line) || `Feature ${idx + 1}`,
      desc: line,
      priority: idx < 2 ? "MVP" : "Next",
    }));
  }
  return [];
}

function parseDeliveryFromString(value: string): DeliveryPhase[] {
  const blocks = value
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  return blocks.map((block, idx) => {
    const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
    const header = lines[0] ?? `Phase ${idx + 1}`;
    const match = header.match(/^(.+?)\s*(?:—|-)\s*(\d+)\s*weeks?/i);
    const phase = match?.[1]?.trim() ?? header;
    const weeks = clampWeeks(match?.[2] ?? 2);
    const outcomes = lines
      .slice(1)
      .map((line) => line.replace(/^-+\s*/, "").trim())
      .filter(Boolean);
    return {
      phase,
      weeks,
      outcomes: outcomes.length ? outcomes : ["Deliver agreed milestone"],
    };
  });
}

function normalizeDeliveryPlan(value: unknown): DeliveryPhase[] {
  if (Array.isArray(value)) {
    return value
      .map((item, idx) => ({
        phase: asString((item as any)?.phase, `Phase ${idx + 1}`),
        weeks: clampWeeks((item as any)?.weeks),
        outcomes: toList((item as any)?.outcomes),
      }))
      .filter((item) => item.phase)
      .map((item) => ({
        ...item,
        outcomes: item.outcomes.length ? item.outcomes : ["Deliver agreed milestone"],
      }));
  }
  if (typeof value === "string") {
    return parseDeliveryFromString(value);
  }
  return [];
}

function parsePricingFromString(value: string): PricingPackage[] {
  const blocks = value
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  return blocks.map((block, idx) => {
    const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
    const header = lines[0] ?? `Package ${idx + 1}`;
    const match = header.match(/^(.+?)\s*(?:—|-)\s*\$?([\d,]+)/);
    const name = match?.[1]?.trim() ?? header;
    const parsedPrice = Number((match?.[2] ?? "").replace(/,/g, ""));
    const price = Number.isFinite(parsedPrice) && parsedPrice > 0 ? parsedPrice : 0;
    const scope = lines
      .slice(1)
      .map((line) => line.replace(/^-+\s*/, "").trim())
      .filter(Boolean);
    return {
      name,
      price,
      scope: scope.length ? scope : ["Scope finalized during discovery"],
    };
  });
}

function normalizePricingPackages(value: unknown): PricingPackage[] {
  if (Array.isArray(value)) {
    return value
      .map((item, idx) => ({
        name: asString((item as any)?.name, `Package ${idx + 1}`),
        price: Number((item as any)?.price) > 0 ? Math.round(Number((item as any)?.price)) : 0,
        scope: toList((item as any)?.scope),
      }))
      .filter((item) => item.name)
      .map((item) => ({
        ...item,
        scope: item.scope.length ? item.scope : ["Scope finalized during discovery"],
      }));
  }
  if (typeof value === "string") {
    return parsePricingFromString(value);
  }
  return [];
}

function safeTextForEdit(value: unknown): string {
  if (Array.isArray(value)) return value.map((item) => asString(item)).filter(Boolean).join("\n");
  return asString(value);
}

function normalizeProposalOutput(raw: unknown): ProposalOutput {
  const fallbackSummary = `App Robot turns this app idea into a practical, build-ready MVP plan with clear next steps.`;
  const fallbackProblem = `Founders need a concrete scope and timeline before investing in app development.`;
  const fallbackSolution = `Define a lean MVP, validate the core workflow, and build with phased delivery checkpoints.`;
  const keyFeatures = normalizeKeyFeatures((raw as any)?.keyFeatures);
  const deliveryPlan = normalizeDeliveryPlan((raw as any)?.deliveryPlan);
  const pricingPackages = normalizePricingPackages((raw as any)?.pricingPackages);

  return {
    executiveSummary: asString((raw as any)?.executiveSummary, fallbackSummary),
    problemStatement: asString((raw as any)?.problemStatement, fallbackProblem),
    solutionOverview: asString((raw as any)?.solutionOverview, fallbackSolution),
    keyFeatures: keyFeatures.length
      ? keyFeatures
      : [
          {
            title: "Core user workflow",
            desc: "Deliver the single user action that proves product value quickly.",
            priority: "MVP",
          },
          {
            title: "User onboarding",
            desc: "Help first-time users understand what to do and get value fast.",
            priority: "MVP",
          },
          {
            title: "Insights and follow-up",
            desc: "Capture engagement and keep users returning with reminders or saved progress.",
            priority: "Next",
          },
        ],
    techStack: toList((raw as any)?.techStack).length
      ? toList((raw as any)?.techStack)
      : ["React Native", "Firebase", "Cloud Functions"],
    deliveryPlan: deliveryPlan.length
      ? deliveryPlan
      : [
          { phase: "Scope & UX", weeks: 1, outcomes: ["Finalize MVP scope", "Confirm user flow"] },
          { phase: "Build", weeks: 3, outcomes: ["Implement core workflow", "Internal QA pass"] },
          { phase: "Launch prep", weeks: 1, outcomes: ["Pilot release", "Track first user feedback"] },
        ],
    pricingPackages: pricingPackages.length
      ? pricingPackages
      : [
          {
            name: "Foundation / Focused MVP",
            price: 42000,
            scope: ["3-screen MVP", "Core backend scope", "Launch-quality QA"],
          },
          {
            name: "Product / Production Product",
            price: 78000,
            scope: ["Expanded user journeys", "Deeper integration coverage", "Release hardening"],
          },
          {
            name: "Strategic / Build Partner",
            price: 135000,
            scope: ["Advanced workflows", "Operational tooling", "Premium polish and rollout"],
          },
        ],
    risksMitigations: toList((raw as any)?.risksMitigations).length
      ? toList((raw as any)?.risksMitigations)
      : [
          "Scope can grow too quickly before the core workflow is validated.",
          "User onboarding may be unclear without focused first-run guidance.",
          "Timeline can slip if integrations are added too early.",
        ],
    acceptanceCriteria: toList((raw as any)?.acceptanceCriteria).length
      ? toList((raw as any)?.acceptanceCriteria)
      : [
          "Users can complete the core workflow end-to-end.",
          "Pilot users can save and return to their data.",
          "Owner can measure first-week usage and feedback.",
        ],
  };
}

function sanitizeFeatures(features: KeyFeature[]): KeyFeature[] {
  return features
    .map((feature) => ({
      title: asString(feature.title),
      desc: asString(feature.desc),
      priority: normalizePriority(feature.priority),
    }))
    .filter((feature) => feature.title || feature.desc);
}

function sanitizeDeliveryPlan(phases: DeliveryPhase[]): DeliveryPhase[] {
  return phases
    .map((phase, idx) => ({
      phase: asString(phase.phase, `Phase ${idx + 1}`),
      weeks: clampWeeks(phase.weeks),
      outcomes: toList(phase.outcomes),
    }))
    .filter((phase) => phase.phase)
    .map((phase) => ({
      ...phase,
      outcomes: phase.outcomes.length ? phase.outcomes : ["Deliver agreed milestone"],
    }));
}

function sanitizePricing(packages: PricingPackage[]): PricingPackage[] {
  return packages
    .map((pkg, idx) => ({
      name: asString(pkg.name, `Package ${idx + 1}`),
      price: Number(pkg.price) > 0 ? Math.round(Number(pkg.price)) : 0,
      scope: toList(pkg.scope),
    }))
    .filter((pkg) => pkg.name)
    .map((pkg) => ({
      ...pkg,
      scope: pkg.scope.length ? pkg.scope : ["Scope finalized during discovery"],
    }));
}

function extractTargetUser(idea: string, problem: string): string {
  const source = `${idea} ${problem}`;
  const match = source.match(/\bfor\s+([^.,;]+)/i);
  if (match?.[1]) return match[1].trim();
  return "Busy founders and operators who need clear execution direction";
}

function extractCoreValue(solutionOverview: string): string {
  const concise = firstSentence(solutionOverview);
  if (concise) return concise;
  return "A clear, build-ready MVP plan with practical next steps";
}

function deriveMonetization(packages: Array<{ name: string }>): string {
  if (!packages.length) return "Use a paid discovery-to-build path and convert into ongoing product support.";
  if (packages.length === 1) return `Lead with a ${packages[0].name} engagement and expand based on validated traction.`;
  const packageNames = packages.slice(0, 2).map((pkg) => pkg.name).join(" and ");
  return `Position a tiered engagement (${packageNames}) to align spend with product ambition and delivery risk.`;
}

function deriveRevenueIdea(recommendedRangeLabel: string): string {
  return `Use ${recommendedRangeLabel} as the planning band for a launch-grade MVP engagement, then expand scope in measured phases once traction is confirmed.`;
}

function improveText(text: string, mode: ImproveMode): string {
  const concise = sentenceLimit(text, 2);
  if (!concise) return text;
  if (mode === "simpler") return concise;
  if (mode === "reduce") return `${concise} Focus on a lean MVP with one clear user workflow first.`;
  return `${concise} Prioritize fastest path to launch with minimal dependencies.`;
}

function improveSectionValue(key: ProposalSectionKey, mode: ImproveMode, output: ProposalOutput): ProposalOutput[ProposalSectionKey] {
  switch (key) {
    case "executiveSummary":
    case "problemStatement":
    case "solutionOverview":
      return improveText(output[key], mode);
    case "techStack":
      if (mode === "simpler") return output.techStack.slice(0, 3);
      if (mode === "reduce") return output.techStack.slice(0, 2);
      return ["React Native", "Firebase"];
    case "risksMitigations":
    case "acceptanceCriteria":
      return output[key].slice(0, mode === "simpler" ? 3 : 2);
    case "keyFeatures": {
      const features = sanitizeFeatures(output.keyFeatures);
      if (mode === "simpler") {
        const simplified: KeyFeature[] = features.map((feature) => ({
          ...feature,
          desc: firstSentence(feature.desc) || feature.desc,
        }));
        return simplified;
      }
      if (mode === "reduce") {
        const reduced: KeyFeature[] = features
          .filter((feature) => feature.priority === "MVP")
          .slice(0, 2)
          .concat(features.filter((feature) => feature.priority === "Next").slice(0, 1));
        return reduced;
      }
      const faster: KeyFeature[] = features.slice(0, 2).map(
        (feature): KeyFeature => ({
          ...feature,
          priority: "MVP",
          desc: firstSentence(feature.desc) || feature.desc,
        })
      );
      return faster;
    }
    case "deliveryPlan": {
      const phases = sanitizeDeliveryPlan(output.deliveryPlan);
      if (mode === "simpler") {
        return phases.map((phase) => ({ ...phase, outcomes: phase.outcomes.slice(0, 2) }));
      }
      if (mode === "reduce") {
        return phases.slice(0, 2).map((phase) => ({ ...phase, outcomes: phase.outcomes.slice(0, 2) }));
      }
      return phases.slice(0, 2).map((phase) => ({
        ...phase,
        weeks: Math.max(1, phase.weeks - 1),
        outcomes: phase.outcomes.slice(0, 2),
      }));
    }
    case "pricingPackages": {
      const packages = sanitizePricing(output.pricingPackages);
      if (mode === "simpler") return packages.map((pkg) => ({ ...pkg, scope: pkg.scope.slice(0, 3) })).slice(0, 2);
      if (mode === "reduce") return packages.slice(0, 1).map((pkg) => ({ ...pkg, scope: pkg.scope.slice(0, 3) }));
      return packages.slice(0, 1).map((pkg) => ({
        ...pkg,
        name: "Foundation / Focused MVP",
        price: pkg.price > 0 ? Math.round(pkg.price * 0.85) : pkg.price,
        scope: pkg.scope.slice(0, 2),
      }));
    }
    default:
      return output[key];
  }
}

export default function ProposalViewer() {
  const [proposal, setProposal] = useState<ProposalDoc | null>(null);
  const [proposalLoadError, setProposalLoadError] = useState<string | null>(null);
  const [loadingStageIndex, setLoadingStageIndex] = useState(0);

  const [editState, setEditState] = useState<EditState | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editFeatures, setEditFeatures] = useState<KeyFeature[]>([]);
  const [editDelivery, setEditDelivery] = useState<DeliveryPhase[]>([]);
  const [editPricing, setEditPricing] = useState<PricingPackage[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [improveBusyKey, setImproveBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [buildPlanStarting, setBuildPlanStarting] = useState(false);
  const [proposalRetrying, setProposalRetrying] = useState(false);

  const navigate = useNavigate();
  const { proposalId } = useParams<{ proposalId: string }>();
  const { isFullySignedIn: canMutate, requireSignedInUser, authModal } = useMutationAuthGate();

  useEffect(() => {
    ensureAnon();
  }, []);

  useEffect(() => {
    if (!proposalId) return;
    setProposalLoadError(null);
    const unsub = watchProposal(
      proposalId,
      (next) => setProposal(next as ProposalDoc),
      (error) => setProposalLoadError((error as Error)?.message ?? "Could not load this proposal.")
    );
    return () => unsub && unsub();
  }, [proposalId]);

  useEffect(() => {
    const status = proposal?.status ?? "generating";
    if (status === "ready" || status === "failed") return;
    setLoadingStageIndex(0);
    const timer = window.setInterval(() => {
      setLoadingStageIndex((previous) => Math.min(previous + 1, LOADING_STAGES.length - 1));
    }, 2200);
    return () => window.clearInterval(timer);
  }, [proposal?.status, proposalId]);

  const output = useMemo(() => normalizeProposalOutput(proposal?.output), [proposal?.output]);
  const pricingAssessment = useMemo(
    () =>
      buildPricingAssessment({
        idea: proposal?.inputs?.idea,
        inputs: proposal?.inputs,
        output,
      }),
    [output, proposal?.inputs]
  );

  const status = proposal?.status ?? "generating";
  const proposalGenerationError =
    (proposal as any)?.lastError || proposal?._error || null;
  const copyrightYear = new Date().getFullYear();
  const statusEmoji: { [key in ProposalStatus]: string } = {
    ready: "✅",
    failed: "❌",
    generating: "⚙️",
    queued: "⏳",
  };

  const targetUser = extractTargetUser(proposal?.inputs?.idea ?? "", output.problemStatement);
  const coreValue = extractCoreValue(output.solutionOverview);
  const topSummary = sentenceLimit(output.executiveSummary, 2) || output.executiveSummary;
  const focusFeature = output.keyFeatures.find((feature) => feature.priority === "MVP") ?? output.keyFeatures[0];
  const mustHaveFeatures = output.keyFeatures.filter((feature) => feature.priority === "MVP");
  const niceToHaveFeatures = output.keyFeatures.filter((feature) => feature.priority === "Next");
  const keyRisks = output.risksMitigations.slice(0, 3);
  const investmentRanges = pricingAssessment.packages.map((tier) => ({
    name: tier.name,
    range: tier.rangeLabel,
    note: tier.summary,
  }));

  function openTextEdit(key: ProposalSectionKey, label: string, value: unknown, kind: "text" | "list") {
    if (analytics) logEvent(analytics, "edit_section_click", { section: key });
    setActionError(null);
    setEditError(null);
    setEditState({ key, label, kind });
    setEditValue(safeTextForEdit(value));
  }

  function openFeatureEdit() {
    if (analytics) logEvent(analytics, "edit_section_click", { section: "keyFeatures" });
    setActionError(null);
    setEditError(null);
    setEditState({ key: "keyFeatures", label: "Core Features", kind: "features" });
    setEditFeatures(sanitizeFeatures(output.keyFeatures));
  }

  function openDeliveryEdit() {
    if (analytics) logEvent(analytics, "edit_section_click", { section: "deliveryPlan" });
    setActionError(null);
    setEditError(null);
    setEditState({ key: "deliveryPlan", label: "Delivery Plan", kind: "delivery" });
    setEditDelivery(sanitizeDeliveryPlan(output.deliveryPlan));
  }

  function openPricingEdit() {
    if (analytics) logEvent(analytics, "edit_section_click", { section: "pricingPackages" });
    setActionError(null);
    setEditError(null);
    setEditState({ key: "pricingPackages", label: "What it takes to build", kind: "pricing" });
    setEditPricing(sanitizePricing(output.pricingPackages));
  }

  async function handleSaveEdit() {
    if (!editState || !proposalId) return;
    setEditSaving(true);
    setEditError(null);
    try {
      let valueToSave: any = null;
      if (editState.kind === "text") {
        valueToSave = asString(editValue);
      } else if (editState.kind === "list") {
        valueToSave = toList(editValue);
      } else if (editState.kind === "features") {
        valueToSave = sanitizeFeatures(editFeatures);
        if (!valueToSave.length) throw new Error("Please keep at least one feature.");
      } else if (editState.kind === "delivery") {
        valueToSave = sanitizeDeliveryPlan(editDelivery);
        if (!valueToSave.length) throw new Error("Please keep at least one delivery phase.");
      } else if (editState.kind === "pricing") {
        valueToSave = sanitizePricing(editPricing);
        if (!valueToSave.length) throw new Error("Please keep at least one investment package.");
      }
      await updateProposalSectionServer(proposalId, editState.key, valueToSave);
      if (analytics) logEvent(analytics, "save_edit_click", { section: editState.key });
      setEditState(null);
    } catch (error) {
      setEditError((error as Error)?.message || "Failed to save this section.");
      if (analytics) logEvent(analytics, "save_edit_failure", { section: editState.key });
    } finally {
      setEditSaving(false);
    }
  }

  async function handleImprove(sectionKey: ProposalSectionKey, mode: ImproveMode) {
    if (!proposalId) return;
    const busyKey = `${sectionKey}:${mode}`;
    setActionError(null);
    setImproveBusyKey(busyKey);
    try {
      const nextValue = improveSectionValue(sectionKey, mode, output);
      await updateProposalSectionServer(proposalId, sectionKey, nextValue);
      if (analytics) logEvent(analytics, "improve_section_click", { section: sectionKey, mode });
    } catch (error) {
      setActionError((error as Error)?.message || "Could not improve this section right now.");
    } finally {
      setImproveBusyKey(null);
    }
  }

  const onRetryProposalGeneration = async () => {
    if (!proposalId || proposalRetrying) return;
    const retryInputs = proposal?.inputs;
    const idea = asString(proposal?.inputs?.idea);
    if (!idea || !retryInputs) {
      setActionError("Could not retry generation because the saved idea is missing.");
      return;
    }
    setActionError(null);
    setProposalRetrying(true);
    if (analytics) logEvent(analytics, "proposal_retry_generation_click", { proposalId });
    try {
      await ensureAnon();
      await genProposalServer(proposalId, retryInputs);
    } catch (error) {
      setActionError((error as Error)?.message || "Could not retry proposal generation right now.");
    } finally {
      setProposalRetrying(false);
    }
  };

  const onBuildStart = async () => {
    if (!proposalId || buildPlanStarting) return;
    setActionError(null);
    setBuildPlanStarting(true);
    if (analytics) logEvent(analytics, "build_start_click", { proposalId });
    let startErrorMessage: string | null = null;
    try {
      await ensureAnon();
      await startDevPlanGenerationServer(proposalId, false);
    } catch (error) {
      startErrorMessage = (error as Error)?.message || "Could not start build plan generation right now.";
    } finally {
      setBuildPlanStarting(false);
      navigate(`/dev-plan/${proposalId}`, {
        state: startErrorMessage ? { startBuildPlanError: startErrorMessage } : undefined,
      });
    }
  };

  const actionButtons = (sectionKey: ProposalSectionKey, onEdit: () => void) => (
    <div className="proposal-actions">
      <button
        className="proposal-link-btn"
        type="button"
        onClick={() => {
          void requireSignedInUser(
            () => onEdit(),
            "Create your account to edit and save proposal changes."
          );
        }}
      >
        Edit
      </button>
      {IMPROVE_ACTIONS.map((action) => {
        const buttonBusyKey = `${sectionKey}:${action.mode}`;
        return (
          <button
            key={buttonBusyKey}
            className="proposal-chip-btn"
            type="button"
            onClick={() => {
              void requireSignedInUser(
                () => handleImprove(sectionKey, action.mode),
                "Create your account to improve and save proposal changes."
              );
            }}
            disabled={improveBusyKey === buttonBusyKey}
          >
            {improveBusyKey === buttonBusyKey ? "Improving…" : action.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="container proposal-page">
      <header className="topbar proposal-topbar">
        <Link to="/" className="proposal-back-link">← New proposal</Link>
        <h2 className="proposal-title">App Robot Plan</h2>
        <span className="proposal-topbar-spacer" />
      </header>

      {proposalLoadError && <div className="error">{proposalLoadError}</div>}
      {actionError && <div className="error">{actionError}</div>}
      {proposalGenerationError && <div className="error">{proposalGenerationError}</div>}

      {!proposal && (
        <div className="proposal-loading-card">
          <h3>Preparing your plan</h3>
          <ul>
            {LOADING_STAGES.map((stage, index) => (
              <li key={stage} className={index <= loadingStageIndex ? "is-active" : ""}>{stage}</li>
            ))}
          </ul>
        </div>
      )}

      {proposal && (
        <>
          <header className="proposal-print-header" aria-hidden="true">
            <div className="proposal-print-header-main">
              <img src={COMPANY_LOGO_URL} alt={`${COMPANY_NAME} logo`} className="proposal-print-logo" />
              <div className="proposal-print-company">
                <strong>{COMPANY_NAME}</strong>
                <a href={COMPANY_WEBSITE}>{COMPANY_WEBSITE}</a>
                <span>{COMPANY_ADDRESS}</span>
                <a href={`mailto:${COMPANY_CONTACT_EMAIL}`}>{COMPANY_CONTACT_EMAIL}</a>
              </div>
            </div>
          </header>
          <footer className="proposal-print-footer" aria-hidden="true">
            <span>&copy; {copyrightYear} {COMPANY_NAME}. All rights reserved.</span>
          </footer>
          <div className="status">Status: <strong>{status}</strong> {statusEmoji[status]}</div>
          {!canMutate && (
            <div className="proposal-loading-card">
              <h3>Read-only preview</h3>
              <p>Sign up to edit, regenerate, and save your plan.</p>
            </div>
          )}

          {status !== "ready" && status !== "failed" && (
            <div className="proposal-loading-card">
              <h3>Building your proposal</h3>
              <ul>
                {LOADING_STAGES.map((stage, index) => (
                  <li key={stage} className={index <= loadingStageIndex ? "is-active" : ""}>{stage}</li>
                ))}
              </ul>
              <p>This page updates automatically.</p>
            </div>
          )}

          {status === "failed" && (
            <div className="proposal-loading-card">
              <h3>We couldn&apos;t generate this plan right now.</h3>
              <p>{proposalGenerationError || "Retry generation using your saved idea."}</p>
              <button
                type="button"
                className="proposal-link-btn"
                onClick={() => void onRetryProposalGeneration()}
                disabled={proposalRetrying}
              >
                {proposalRetrying ? "Retrying…" : "Try again"}
              </button>
            </div>
          )}

          {status === "ready" && (
            <>
              <div className="proposal-utility-actions">
                <button
                  className="proposal-utility-btn"
                  type="button"
                  onClick={() => {
                    if (analytics) logEvent(analytics, "print_pdf_click");
                    window.print();
                  }}
                >
                  Print / Save PDF
                </button>
                <button
                  className="proposal-utility-btn proposal-utility-btn-secondary"
                  type="button"
                  onClick={async () => {
                    if (analytics) logEvent(analytics, "copy_link_click");
                    await navigator.clipboard.writeText(window.location.href);
                    alert("Link copied to clipboard.");
                  }}
                >
                  Copy Link
                </button>
              </div>

              <article className="proposal decision-proposal">
                <section className="proposal-section-card">
                  <div className="proposal-section-header">
                    <h3>Here&apos;s your app plan</h3>
                    {actionButtons("executiveSummary", () =>
                      openTextEdit("executiveSummary", "Top Summary", output.executiveSummary, "text")
                    )}
                  </div>
                  <p>{topSummary}</p>
                  <div className="proposal-summary-grid">
                    <div>
                      <span>Target user</span>
                      <strong>{targetUser}</strong>
                    </div>
                    <div>
                      <span>Core value</span>
                      <strong>{coreValue}</strong>
                    </div>
                  </div>
                </section>

                <section className="proposal-section-card">
                  <div className="proposal-section-header">
                    <h3>Business Potential</h3>
                    {actionButtons("pricingPackages", openPricingEdit)}
                  </div>
                  <ul>
                    <li><strong>Target customer:</strong> {targetUser}</li>
                    <li><strong>Engagement model:</strong> {deriveMonetization(pricingAssessment.packages)}</li>
                    <li><strong>Commercial planning note:</strong> {deriveRevenueIdea(pricingAssessment.recommendedRange.label)}</li>
                  </ul>
                </section>

                <section className="proposal-section-card">
                  <div className="proposal-section-header">
                    <h3>What could go wrong</h3>
                    {actionButtons("risksMitigations", () =>
                      openTextEdit("risksMitigations", "Key Risks", output.risksMitigations, "list")
                    )}
                  </div>
                  <ul>
                    {keyRisks.map((risk, index) => (
                      <li key={`${risk}-${index}`}>{risk}</li>
                    ))}
                  </ul>
                </section>

                <section className="proposal-section-card">
                  <div className="proposal-section-header">
                    <h3>Core Features</h3>
                    {actionButtons("keyFeatures", openFeatureEdit)}
                  </div>
                  <div className="proposal-feature-groups">
                    <div>
                      <h4>Must-have</h4>
                      <ul>
                        {(mustHaveFeatures.length ? mustHaveFeatures : output.keyFeatures.slice(0, 2)).map((feature, index) => (
                          <li key={`mvp-${feature.title}-${index}`}>
                            <strong>{feature.title}</strong>
                            <span>{feature.desc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4>Nice-to-have</h4>
                      <ul>
                        {(niceToHaveFeatures.length ? niceToHaveFeatures : output.keyFeatures.slice(2)).map((feature, index) => (
                          <li key={`next-${feature.title}-${index}`}>
                            <strong>{feature.title}</strong>
                            <span>{feature.desc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>

                <section className="proposal-section-card">
                  <div className="proposal-section-header">
                    <h3>If we were building this, we&apos;d start with:</h3>
                    {actionButtons("solutionOverview", () =>
                      openTextEdit("solutionOverview", "MVP Recommendation", output.solutionOverview, "text")
                    )}
                  </div>
                  <p>
                    We&apos;d start with a lean 3-screen MVP: a Home screen, a Focus Feature screen centered on the core
                    user action, and a User Profile screen for user-specific settings and history.
                  </p>
                  <ul>
                    <li><strong>Home Screen:</strong> main dashboard, navigation anchor, and top priorities.</li>
                    <li>
                      <strong>Focus Feature Screen:</strong> centered on <strong>{focusFeature?.title || "your highest-value user action"}</strong> with
                      clear completion flow.
                    </li>
                    <li><strong>User Profile Screen:</strong> account details, preferences, saved history, and controls.</li>
                  </ul>
                </section>

                <section className="proposal-section-card">
                  <div className="proposal-section-header">
                    <h3>Delivery Plan</h3>
                    {actionButtons("deliveryPlan", openDeliveryEdit)}
                  </div>
                  <ol className="proposal-phase-list">
                    {output.deliveryPlan.map((phase, index) => (
                      <li key={`${phase.phase}-${index}`}>
                        <div className="proposal-phase-header">
                          <strong>{phase.phase}</strong>
                          <span>{phase.weeks} week{phase.weeks === 1 ? "" : "s"}</span>
                        </div>
                        <ul>
                          {phase.outcomes.map((outcome, outcomeIndex) => (
                            <li key={`${outcome}-${outcomeIndex}`}>{outcome}</li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ol>
                </section>

                <section className="proposal-section-card">
                  <div className="proposal-section-header">
                    <h3>What it takes to build</h3>
                    {actionButtons("pricingPackages", openPricingEdit)}
                  </div>
                  <p>This is a realistic estimate to design, build, and launch the MVP described above.</p>
                  <p className="proposal-investment-recommended">
                    <strong>Recommended build path:</strong> {pricingAssessment.recommendedTierLabel} ({pricingAssessment.recommendedRange.label})
                  </p>
                  <p className="proposal-investment-copy">{pricingAssessment.rangeExplanation}</p>
                  <p className="proposal-investment-copy">{pricingAssessment.riskNote}</p>
                  <p className="proposal-investment-copy">
                    Final investment depends on scope clarity, integration depth, quality bar, and delivery shape.
                  </p>
                  <p className="proposal-investment-confidence">
                    {pricingAssessment.confidenceLabel} ({pricingAssessment.confidenceScore}/100)
                  </p>
                  <ul className="proposal-investment-list">
                    {investmentRanges.map((item, index) => (
                      <li key={`${item.name}-${index}`}>
                        <div>
                          <strong>{item.name}</strong>
                          <span>{item.range}</span>
                        </div>
                        <p>{item.note}</p>
                      </li>
                    ))}
                  </ul>
                  <h4 className="proposal-investment-rationale-title">Investment rationale</h4>
                  <ul>
                    {pricingAssessment.rationale.map((point, index) => (
                      <li key={`pricing-rationale-${index}`}>{point}</li>
                    ))}
                  </ul>
                  <p>If you want exact pricing and a build-ready breakdown, continue to the build plan.</p>
                  <button
                    type="button"
                    className="proposal-link-btn"
                    onClick={() => void onBuildStart()}
                    disabled={buildPlanStarting}
                  >
                    {buildPlanStarting ? "Preparing build plan…" : "Turn this into a build plan"}
                  </button>
                </section>
              </article>

              <section className="proposal-final-cta">
                <h3>Let&apos;s make this build-ready</h3>
                <p>Turn this into a clear MVP scope, timeline, and next steps.</p>
                <div className="proposal-final-cta-buttons">
                  <button type="button" onClick={() => void onBuildStart()} disabled={buildPlanStarting}>
                    {buildPlanStarting ? "Starting build plan…" : "View Build Plan"}
                  </button>
                  <button
                    type="button"
                  className="proposal-secondary-btn"
                  onClick={() => {
                    void requireSignedInUser(
                      () => openFeatureEdit(),
                      "Create your account to edit and refine this plan."
                    );
                  }}
                  >
                    Refine This Plan
                  </button>
                </div>
              </section>
            </>
          )}
        </>
      )}

      {editState && (
        <div className="proposal-modal-overlay">
          <div className="proposal-modal">
            <h2>Edit {editState.label}</h2>

            {(editState.kind === "text" || editState.kind === "list") && (
              <textarea
                value={editValue}
                onChange={(event) => setEditValue(event.target.value)}
                rows={10}
                className="proposal-modal-textarea"
              />
            )}

            {editState.kind === "features" && (
              <div className="proposal-editor-grid">
                {editFeatures.map((feature, index) => (
                  <div key={`feature-${index}`} className="proposal-editor-card">
                    <div className="proposal-editor-row">
                      <label>Priority</label>
                      <select
                        value={feature.priority}
                        onChange={(event) =>
                          setEditFeatures((previous) =>
                            previous.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, priority: normalizePriority(event.target.value) }
                                : item
                            )
                          )
                        }
                      >
                        <option value="MVP">Must-have</option>
                        <option value="Next">Nice-to-have</option>
                      </select>
                    </div>
                    <input
                      value={feature.title}
                      onChange={(event) =>
                        setEditFeatures((previous) =>
                          previous.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, title: event.target.value } : item
                          )
                        )
                      }
                      placeholder="Feature name"
                    />
                    <textarea
                      rows={3}
                      value={feature.desc}
                      onChange={(event) =>
                        setEditFeatures((previous) =>
                          previous.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, desc: event.target.value } : item
                          )
                        )
                      }
                      placeholder="What this feature does"
                    />
                    <button
                      type="button"
                      className="proposal-link-btn"
                      onClick={() => setEditFeatures((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="proposal-chip-btn"
                  onClick={() =>
                    setEditFeatures((previous) => [
                      ...previous,
                      { title: "", desc: "", priority: "MVP" },
                    ])
                  }
                >
                  Add Feature
                </button>
              </div>
            )}

            {editState.kind === "delivery" && (
              <div className="proposal-editor-grid">
                {editDelivery.map((phase, index) => (
                  <div key={`phase-${index}`} className="proposal-editor-card">
                    <input
                      value={phase.phase}
                      onChange={(event) =>
                        setEditDelivery((previous) =>
                          previous.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, phase: event.target.value } : item
                          )
                        )
                      }
                      placeholder="Phase title"
                    />
                    <div className="proposal-editor-row">
                      <label>Weeks</label>
                      <input
                        type="number"
                        min={1}
                        value={phase.weeks}
                        onChange={(event) =>
                          setEditDelivery((previous) =>
                            previous.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, weeks: clampWeeks(event.target.value) } : item
                            )
                          )
                        }
                      />
                    </div>
                    <textarea
                      rows={4}
                      value={phase.outcomes.join("\n")}
                      onChange={(event) =>
                        setEditDelivery((previous) =>
                          previous.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, outcomes: toList(event.target.value) } : item
                          )
                        )
                      }
                      placeholder="One outcome per line"
                    />
                    <button
                      type="button"
                      className="proposal-link-btn"
                      onClick={() => setEditDelivery((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="proposal-chip-btn"
                  onClick={() =>
                    setEditDelivery((previous) => [
                      ...previous,
                      { phase: `Phase ${previous.length + 1}`, weeks: 1, outcomes: ["Deliver agreed milestone"] },
                    ])
                  }
                >
                  Add Phase
                </button>
              </div>
            )}

            {editState.kind === "pricing" && (
              <div className="proposal-editor-grid">
                {editPricing.map((pkg, index) => (
                  <div key={`package-${index}`} className="proposal-editor-card">
                    <input
                      value={pkg.name}
                      onChange={(event) =>
                        setEditPricing((previous) =>
                          previous.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, name: event.target.value } : item
                          )
                        )
                      }
                      placeholder="Package name"
                    />
                    <div className="proposal-editor-row">
                      <label>Anchor price (internal)</label>
                      <input
                        type="number"
                        min={0}
                        value={pkg.price}
                        onChange={(event) =>
                          setEditPricing((previous) =>
                            previous.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, price: Math.max(0, Math.round(Number(event.target.value) || 0)) }
                                : item
                            )
                          )
                        }
                      />
                    </div>
                    <textarea
                      rows={4}
                      value={pkg.scope.join("\n")}
                      onChange={(event) =>
                        setEditPricing((previous) =>
                          previous.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, scope: toList(event.target.value) } : item
                          )
                        )
                      }
                      placeholder="One scope item per line"
                    />
                    <button
                      type="button"
                      className="proposal-link-btn"
                      onClick={() => setEditPricing((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="proposal-chip-btn"
                  onClick={() =>
                    setEditPricing((previous) => [
                      ...previous,
                      { name: `Package ${previous.length + 1}`, price: 0, scope: ["Scope finalized during discovery"] },
                    ])
                  }
                >
                  Add Package
                </button>
              </div>
            )}

            {editError && <div className="error">{editError}</div>}

            <div className="proposal-modal-actions">
              <button
                type="button"
                className="proposal-secondary-btn"
                onClick={() => {
                  if (analytics) logEvent(analytics, "edit_cancel_click", { section: editState.key });
                  setEditState(null);
                  setEditError(null);
                }}
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void requireSignedInUser(
                    () => handleSaveEdit(),
                    "Create your account to save proposal changes."
                  );
                }}
                disabled={editSaving}
              >
                {editSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {authModal}
      <Footer />
    </div>
  );
}
